import { ApolloError, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { AddOrganizationOwners } from 'Apollo';
import { Form, SubmitButton, TextInput } from 'Components/Shared';
import TextInputEmails from 'Components/Shared/Form/Fields/TextInputEmails';
import useFormValidation from 'Hooks/useFormValidation';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { useState } from 'react';

const MESSAGE_MAX_LENGTH = 1000;
const MAX_OWNERS = 5;

type InnerError = {
  code?: string;
  message?: string;
  data?: { identifier?: string };
};

type FailedInvitation = { email: string; message: string };

type FormValues = { emails: string[]; message: string };

export type AddOrganizationOwnersModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly organizationCode: string;
  readonly organizationName: string;
  /** Called after at least one owner has been added, so the parent can refresh its data. */
  readonly onOwnersAdded?: () => void;
};

export function AddOrganizationOwnersModal({
  open,
  onClose,
  organizationCode,
  organizationName,
  onOwnersAdded
}: AddOrganizationOwnersModalProps) {
  const ts = useTranslate();
  const { onError, onSuccess } = useNotification();
  const { organizationOwners } = useFormValidation();
  const [failedInvitations, setFailedInvitations] = useState<FailedInvitation[] | null>(null);

  const [addOwners, { loading }] = useMutation(AddOrganizationOwners);

  const mapErrorMessage = (inner: InnerError): string => {
    if (inner.code === 'E105') {
      return ts('page.organizations.addOwners.userDoesNotExist');
    }
    return inner.message ?? ts('page.organizations.addOwners.genericError');
  };

  const handleClose = () => {
    if (!loading) {
      setFailedInvitations(null);
      onClose();
    }
  };

  const handleSubmit = async (values: FormValues) => {
    const trimmedMessage = values.message.trim();
    try {
      await addOwners({
        variables: {
          organizationCode,
          emails: values.emails,
          message: trimmedMessage.length > 0 ? trimmedMessage : null
        }
      });
      onSuccess('page.organizations.addOwners.success');
      onOwnersAdded?.();
      onClose();
    } catch (e) {
      const apolloError = e as ApolloError;
      const inner = (apolloError.graphQLErrors?.[0]?.extensions?.innerErrors as InnerError[] | undefined) ?? [];
      if (inner.length > 0) {
        const failed = inner.map(err => ({
          email: err.data?.identifier ?? '',
          message: mapErrorMessage(err)
        }));
        const failedEmails = new Set(failed.map(f => f.email));
        const addedCount = values.emails.filter(email => !failedEmails.has(email)).length;
        setFailedInvitations(failed);
        if (addedCount > 0) {
          onOwnersAdded?.();
        }
      } else {
        onError(apolloError);
      }
    }
  };

  const renderResult = () => (
    <>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          {ts('page.organizations.addOwners.errorsTitle')}
        </Typography>
        {failedInvitations!.map(failure => (
          <Alert severity="error" sx={{ mb: 1 }} key={failure.email}>
            <b>{failure.email}</b> — {failure.message}
          </Alert>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose}>
          {ts('common.action.close')}
        </Button>
      </DialogActions>
    </>
  );

  const renderForm = () => (
    <Form<FormValues>
      key={open ? 'open' : 'closed'}
      initialValues={{ emails: [], message: '' }}
      validationSchema={organizationOwners}
      onSubmit={handleSubmit}
    >
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {ts('page.organizations.addOwners.infoBanner', { max: MAX_OWNERS })}
        </Alert>
        <TextInputEmails
          name="emails"
          size="medium"
          multiline
          label={ts('page.organizations.addOwners.emailLabel')}
          helperText={ts('page.organizations.addOwners.emailPlaceholder')}
        />
        <TextInput
          name="message"
          fullWidth
          margin="normal"
          multiline
          minRows={4}
          inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
          label={ts('page.organizations.addOwners.messageLabel')}
          placeholder={ts('page.organizations.addOwners.messagePlaceholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {ts('common.action.cancel')}
        </Button>
        <SubmitButton
          title={ts('page.organizations.addOwners.send')}
          loading={loading}
        />
      </DialogActions>
    </Form>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {ts('page.organizations.addOwners.modalTitle', { organizationName })}
      </DialogTitle>
      {failedInvitations ? renderResult() : renderForm()}
    </Dialog>
  );
}

export default AddOrganizationOwnersModal;
